// Pre-insert duplicate detection for the contact database (spec §5).
// DECISION: the duplicate alert keys on COMPANY NAME similarity. Two records are
// treated as the same "database" when their normalized company names are at least
// SIMILARITY_THRESHOLD similar (fuzzy) — email/phone/domain are NOT used.
// Pure functions so they can be unit-tested.

// ≥ 50% similar → treat as duplicate. Tune here if it's too loose/strict.
export const SIMILARITY_THRESHOLD = 0.5

export function normCompany(s) {
  return (s || '').toString().trim().toLowerCase().replace(/\b(pt|cv|tbk|persero|pt\.)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim()
}
// Kept for callers/tests that still normalize these fields elsewhere.
export function normEmail(s) {
  return (s || '').toString().trim().toLowerCase()
}
export function normPhone(s) {
  let p = (s || '').toString().replace(/\D/g, '')
  if (p.startsWith('0')) p = '62' + p.slice(1)   // local -> intl
  if (p.startsWith('620')) p = '62' + p.slice(3)
  return p
}
export function normDomain(s) {
  return (s || '').toString().trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')
}

// Levenshtein edit distance (iterative, O(n*m)).
function levenshtein(a, b) {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 0; i < a.length; i++) {
    let cur = [i + 1]
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1
      cur[j + 1] = Math.min(cur[j] + 1, prev[j + 1] + 1, prev[j] + cost)
    }
    prev = cur
  }
  return prev[b.length]
}
// Character-level similarity ratio 0..1.
function levRatio(a, b) {
  const max = Math.max(a.length, b.length)
  return max === 0 ? 0 : 1 - levenshtein(a, b) / max
}
// Word-set overlap (Jaccard) 0..1.
function tokenJaccard(a, b) {
  const A = new Set(a.split(' ').filter(Boolean))
  const B = new Set(b.split(' ').filter(Boolean))
  if (A.size === 0 || B.size === 0) return 0
  let inter = 0
  for (const t of A) if (B.has(t)) inter++
  return inter / (A.size + B.size - inter)
}

// Combined company-name similarity 0..1. Uses the strongest of: exact,
// containment (one name inside the other), word overlap, and edit distance.
export function companySimilarity(a, b) {
  const x = normCompany(a), y = normCompany(b)
  if (!x || !y) return 0
  if (x === y) return 1
  if (x.includes(y) || y.includes(x)) return Math.max(0.9, tokenJaccard(x, y), levRatio(x, y))
  return Math.max(tokenJaccard(x, y), levRatio(x, y))
}

// matchType: 'none' | 'company'. Returns the best fuzzy company match whose
// similarity reaches `threshold`.
export function findDuplicate(candidate, existingRecords = [], threshold = SIMILARITY_THRESHOLD) {
  const cCompany = normCompany(candidate.company)
  if (!cCompany) return { matchType: 'none', record: null, on: null, score: 0 }
  let best = null, bestScore = 0
  for (const rec of existingRecords) {
    const s = companySimilarity(candidate.company, rec.company)
    if (s > bestScore) { bestScore = s; best = rec }
  }
  if (best && bestScore >= threshold) {
    return { matchType: 'company', record: best, on: bestScore >= 1 ? 'company' : 'similar', score: bestScore }
  }
  return { matchType: 'none', record: null, on: null, score: bestScore }
}

// The agent to name in the alert: prefer original creator, else current owner.
export function conflictAgentName(record) {
  if (!record) return ''
  return record.created_by_agent || record.owner_agent || record.agent || ''
}

// Exact business alert message (spec §5). [Nama Agent] = original creator
// (or current owner) of the existing record.
export function buildDuplicateAlert(record) {
  const name = conflictAgentName(record) || 'agent lain'
  return `ALERT! Database ini sudah ada di sistem dan sudah pernah diinput oleh ${name}. Harap hubungi Dewa Admin untuk melakukan transfer database.`
}

// Decide what the UI should do for a candidate created by `currentAgent`.
//  - block:         similar company owned by someone else -> show alert, cannot insert
//  - own-duplicate: similar company the current agent already owns -> confirm
//  - allow:         no sufficiently-similar company
export function evaluateCandidate(candidate, existingRecords, currentAgent) {
  const dup = findDuplicate(candidate, existingRecords)
  if (dup.matchType === 'none') return { action: 'allow', ...dup }

  const owner = dup.record.owner_agent || dup.record.agent || null
  const ownedByOther = owner && owner !== currentAgent
  return ownedByOther
    ? { action: 'block', alert: buildDuplicateAlert(dup.record), ...dup }
    : { action: 'own-duplicate', ...dup }
}
